// Access model for the Users page: account state, what each role can do, and
// which changes the server will refuse. Pure; checked by access.check.ts.
// The permission matrix mirrors the RequireRole guards in cmd/api/main.go --
// change one, change the other.
import type { Role } from "./types";

export const ROLES: Role[] = ["admin", "noc", "viewer", "portviewer"];

/** A cell of the matrix: yes, no, or yes-with-a-limit (the key names the limit). */
export type Grant = true | false | "masked" | "assigned" | "superadmin";

export type Perm = { key: string; group: "monitor" | "operate" | "configure" | "access"; grant: Record<Role, Grant> };

const all = { admin: true, noc: true, viewer: true, portviewer: false } as const;
const ops = { admin: true, noc: true, viewer: false, portviewer: false } as const;
const adm = { admin: true, noc: false, viewer: false, portviewer: false } as const;

export const PERMS: Perm[] = [
  { key: "network", group: "monitor", grant: all },
  { key: "devices", group: "monitor", grant: all },
  { key: "alerts", group: "monitor", grant: all },
  { key: "customers", group: "monitor", grant: { admin: true, noc: true, viewer: "masked", portviewer: false } },
  { key: "graphs", group: "monitor", grant: { admin: true, noc: true, viewer: true, portviewer: "assigned" } },
  { key: "ack", group: "operate", grant: ops },
  { key: "reboot", group: "operate", grant: ops },
  { key: "ontEdit", group: "operate", grant: ops },
  { key: "wan", group: "operate", grant: ops },
  { key: "olts", group: "configure", grant: adm },
  { key: "acs", group: "configure", grant: adm },
  { key: "integrations", group: "configure", grant: adm },
  { key: "rules", group: "configure", grant: adm },
  { key: "linkmap", group: "configure", grant: adm },
  { key: "shares", group: "configure", grant: adm },
  { key: "users", group: "access", grant: adm },
  { key: "resetOthers", group: "access", grant: { admin: "superadmin", noc: false, viewer: false, portviewer: false } },
  { key: "ownPassword", group: "access", grant: { admin: true, noc: true, viewer: true, portviewer: true } },
];

/** What moving from one role to another adds and removes, for the confirmation. */
export function roleDiff(from: Role, to: Role): { gains: string[]; losses: string[] } {
  // Owner-only rights come from the account flag, never from a role change.
  const has = (g: Grant) => g !== false && g !== "superadmin";
  return {
    gains: PERMS.filter((p) => has(p.grant[to]) && (!has(p.grant[from]) || (p.grant[from] !== true && p.grant[to] === true))).map((p) => p.key),
    losses: PERMS.filter((p) => has(p.grant[from]) && (!has(p.grant[to]) || (p.grant[to] !== true && p.grant[from] === true))).map((p) => p.key),
  };
}

/** Moving to admin, or giving a port viewer the network: needs the strong confirmation. */
export const isEscalation = (from: Role, to: Role) => ROLES.indexOf(to) < ROLES.indexOf(from);

export type AccessUser = {
  id: number; email: string; role: Role; is_superadmin?: boolean;
  disabled_at: string | null; created_at: string;
  last_login_at: string | null; last_active_at?: string | null;
};

export type AccountState = "active" | "inactive" | "disabled";
export const INACTIVE_DAYS = 90;
/** A never-used account is only "inactive" once it had time to be used. */
export const NEVER_USED_DAYS = 30;

const DAY = 86_400_000;

export function accountState(u: AccessUser, nowMs: number): AccountState {
  if (u.disabled_at) return "disabled";
  const last = u.last_active_at ?? u.last_login_at;
  if (last) return nowMs - Date.parse(last) > INACTIVE_DAYS * DAY ? "inactive" : "active";
  return nowMs - Date.parse(u.created_at) > NEVER_USED_DAYS * DAY ? "inactive" : "active";
}

/**
 * Why the server would refuse a change, or null. Same rules as the API's
 * guardAdminLockout and superadmin checks, so the UI explains before the
 * click instead of after it.
 */
export type Block = "self" | "lastAdmin" | "owner" | "notOwner" | "ownerTarget" | null;

export function blockRoleChange(target: AccessUser, me: { id: number }, users: AccessUser[]): Block {
  if (target.id === me.id) return "self";
  if (target.is_superadmin) return "owner";
  if (target.role === "admin" && !target.disabled_at && enabledAdmins(users) <= 1) return "lastAdmin";
  return null;
}

export function blockDisable(target: AccessUser, me: { id: number }, users: AccessUser[]): Block {
  if (target.id === me.id) return "self";
  if (target.is_superadmin) return "owner";
  if (target.role === "admin" && enabledAdmins(users) <= 1) return "lastAdmin";
  return null;
}

/** Setting someone else's password is the owner's alone, and never another owner's. */
export function blockReset(target: AccessUser, me: { id: number; is_superadmin?: boolean }): Block {
  if (!me.is_superadmin) return "notOwner";
  if (target.is_superadmin && target.id !== me.id) return "ownerTarget";
  return null;
}

export const enabledAdmins = (users: AccessUser[]) => users.filter((u) => u.role === "admin" && !u.disabled_at).length;

/** Port viewers are people outside the NOC: customers, partners. */
export const isExternal = (r: Role) => r === "portviewer";
